import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { TimeregistrationService } from '@src/app/helpers/timeregistration/timeregistration.service';
import { formatMinutesAsHours } from '@src/app/helpers/timeregistration/hours.util';
import { TaskEstimate } from '@src/app/models/taskestimate';
import { TimeEntry } from '@src/app/models/timeentry';
/**
 * Estimate + log-time panel shared by any task detail view (project tasks
 * via taskeditmodal, adhoc tasks via adhoc.component). Pass projectId for a
 * project task; leave it out for an adhoc one and this calls the adhoc
 * routes instead. Only rendered by the caller once the task actually has a
 * taskId — a brand-new, unsaved task has nothing to attach an estimate to.
 */
@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-time-tracking-panel',
  templateUrl: './timetrackingpanel.component.html',
  styleUrls: ['./timetrackingpanel.component.css'],
})
export class TimetrackingpanelComponent implements OnChanges {
  @Input() taskId: string;
  @Input() projectId: string;

  private readonly timeregSrv = inject(TimeregistrationService);

  loading = false;
  errorMessage: string;
  estimates: TaskEstimate[] = [];
  timeEntries: TimeEntry[] = [];
  newEstimateHours: number;
  newEstimateComment: string;
  newEntryDate: string = new Date().toISOString().substring(0, 10);
  newEntryHours: number;
  newEntryNote: string;
  estimatePanelOpen = false;
  logPanelOpen = false;

  private get isAdhoc(): boolean {
    return !this.projectId;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.taskId && this.taskId) {
      this.reset();
      this.load();
    }
  }

  private reset() {
    this.estimates = [];
    this.timeEntries = [];
    this.errorMessage = undefined;
    this.newEstimateHours = undefined;
    this.newEstimateComment = undefined;
    this.newEntryDate = new Date().toISOString().substring(0, 10);
    this.newEntryHours = undefined;
    this.newEntryNote = undefined;
    this.estimatePanelOpen = false;
    this.logPanelOpen = false;
  }

  toggleEstimatePanel() {
    this.estimatePanelOpen = !this.estimatePanelOpen;
    if (this.estimatePanelOpen) {
      this.logPanelOpen = false;
    }
  }

  toggleLogPanel() {
    this.logPanelOpen = !this.logPanelOpen;
    if (this.logPanelOpen) {
      this.estimatePanelOpen = false;
    }
  }

  load() {
    if (!this.taskId) {
      return;
    }
    this.loading = true;

    const estimates$ = this.isAdhoc
      ? this.timeregSrv.getAdhocTaskEstimates(this.taskId)
      : this.timeregSrv.getTaskEstimates(this.projectId, this.taskId);
    const entries$ = this.isAdhoc
      ? this.timeregSrv.getAdhocTaskTimeEntries(this.taskId)
      : this.timeregSrv.getTaskTimeEntries(this.projectId, this.taskId);

    estimates$
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (estimates) => (this.estimates = estimates || []),
        error: (err) => console.error('[TimeTrackingPanel] Failed to load estimates', err),
      });
    entries$
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (entries) => {
          this.timeEntries = entries || [];
          this.loading = false;
        },
        error: (err) => {
          console.error('[TimeTrackingPanel] Failed to load time entries', err);
          this.loading = false;
        },
      });
  }

  // Current estimate = last entry (list comes back oldest first); 0 (not
  // undefined) once there is a history, so an explicit re-estimate to 0 reads
  // as "0h", not "unscoped".
  currentEstimateMinutes(): number | undefined {
    return this.estimates.length ? this.estimates[this.estimates.length - 1].minutes : undefined;
  }

  loggedMinutes(): number {
    return this.timeEntries.reduce((sum, entry) => sum + (entry.minutes || 0), 0);
  }

  remainingMinutes(): number | undefined {
    const current = this.currentEstimateMinutes();
    return current === undefined ? undefined : Math.max(0, current - this.loggedMinutes());
  }

  minutesToHours(minutes: number): string {
    return formatMinutesAsHours(minutes, '.');
  }

  addEstimate() {
    if (!this.newEstimateHours || this.newEstimateHours < 0) {
      this.errorMessage = 'Enter an estimate in hours.';
      return;
    }
    const minutes = Math.round(this.newEstimateHours * 60);
    this.errorMessage = undefined;

    const request$ = this.isAdhoc
      ? this.timeregSrv.addAdhocTaskEstimate(this.taskId, minutes, this.newEstimateComment)
      : this.timeregSrv.addTaskEstimate(this.projectId, this.taskId, minutes, this.newEstimateComment);

    request$
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (estimate) => {
          this.estimates = [...this.estimates, estimate];
          this.newEstimateHours = undefined;
          this.newEstimateComment = undefined;
        },
        error: (err) => {
          console.error('[TimeTrackingPanel] Failed to add estimate', err);
          this.errorMessage = "Couldn't save the estimate.";
        },
      });
  }

  logTime() {
    if (!this.newEntryDate || !this.newEntryHours || this.newEntryHours <= 0) {
      this.errorMessage = 'Enter a date and hours to log time.';
      return;
    }
    const minutes = Math.round(this.newEntryHours * 60);
    this.errorMessage = undefined;

    const request$ = this.isAdhoc
      ? this.timeregSrv.logAdhocTimeEntry(this.taskId, this.newEntryDate, minutes, this.newEntryNote)
      : this.timeregSrv.logTimeEntry(this.projectId, this.taskId, this.newEntryDate, minutes, this.newEntryNote);

    request$
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (entry) => {
          this.timeEntries = [entry, ...this.timeEntries];
          this.newEntryHours = undefined;
          this.newEntryNote = undefined;
        },
        error: (err) => {
          console.error('[TimeTrackingPanel] Failed to log time', err);
          this.errorMessage = "Couldn't save the time entry.";
        },
      });
  }

  isMyTimeEntry(entry: TimeEntry): boolean {
    return entry.personId === SessionHelper.getUserSub();
  }

  deleteTimeEntry(entry: TimeEntry) {
    this.timeregSrv.deleteTimeEntry(entry.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.timeEntries = this.timeEntries.filter((e) => e.id !== entry.id);
        },
        error: (err) => {
          console.error('[TimeTrackingPanel] Failed to delete time entry', err);
          this.errorMessage = "Couldn't delete that entry.";
        },
      });
  }
}
